import { useNavigate } from 'react-router';
import { useGlobalStore } from '../global-store/use-store';
import { jwtDecode } from 'jwt-decode';
import { PATH } from '@old/data/core/paths';
import { useToggle } from './core';
import { userAuthService } from '@old/services/domain/auth/authentication';
import { refreshCredentialService } from '@old/services/domain/auth/refresh-auth-credential';

type LoginParams = {
  username: string;
  password: string;
  fromPath: string | null;
};

type useAuthResponse = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (data: LoginParams) => void;
  logout: () => void;
  refreshLogin: () => void;
  isLoading: boolean;
  isError: boolean;
};

export function useAuth(): useAuthResponse {
  const {
    accessToken,
    refreshToken,
    permissions,
    setAccessToken,
    setRefreshToken,
    setPermissions,
    setExpirationTokenDate,
    clearAuthData,
    setOrganizationId,
    setUserOrganizationId,
    setWasTutorialShow,
    setUserFullname,
    setWas_agrotoken_integration_popup_shown,
    setUserID,
    setOrganizationName,
    setAwsTempCredentials,
    setOrganizationTaxId,
    setOrganizationLogo,
    setLanguage,
    setEmail,
    setCountryId,
    setCorvusIntegrated,
    setWas_lasegunda_integration_popup_shown,
    setWas_warrant_popup_shown,
    setWas_rus_popup_shown,
    setWas_galicia_popup_shown,
    setWas_fed_patronal_popup_shown,
    setWas_swissre_popup_shown,
    setSelectedOrganizationName,
  } = useGlobalStore();

  const navigate = useNavigate();

  const [isLoading, toggleLoading] = useToggle(false);
  const [isError, toggleError] = useToggle(false);

  const login = async ({ username, password, fromPath }: LoginParams): Promise<void> => {
    toggleLoading(true);
    toggleError(false);

    try {
      const { summary } = await userAuthService({ body: { username, password } });
      const preferedLanguageId = summary?.prefered_language_id === 1 ? 'es' : 'pt';
      localStorage.setItem('LANGUAGE_PREFERENCE', preferedLanguageId);
      setLanguage(preferedLanguageId);
      setPermissions(summary?.permissions);
      setOrganizationId(summary?.permissions?.organization?.id);
      setUserOrganizationId(summary?.permissions?.organization?.id);
      setWasTutorialShow(summary?.was_tutorial_shown);
      setWas_agrotoken_integration_popup_shown(summary?.was_agrotoken_integration_popup_shown);
      setWas_lasegunda_integration_popup_shown(summary?.was_la_segunda_popup_shown);
      setWas_warrant_popup_shown(summary?.was_warrant_popup_shown);
      setWas_galicia_popup_shown(summary?.was_galicia_popup_shown);
      setWas_fed_patronal_popup_shown(summary?.was_federacion_patronal_popup_shown);
      setWas_rus_popup_shown(summary?.was_rus_popup_shown);
      setWas_swissre_popup_shown(summary?.was_swissre_popup_shown);
      setUserFullname(summary.userFullname);
      setUserID(summary?.userID);
      setEmail(summary?.email);
      setOrganizationTaxId(summary.organizationTaxId);
      setOrganizationName(summary.organizationName);
      setSelectedOrganizationName(summary.organizationName);
      setAwsTempCredentials(summary.awsTempCredentials);
      setOrganizationLogo(summary.organizationLogo);
      setCountryId(summary?.country_id);
      setCorvusIntegrated(summary?.corvusIntegration);
      if (summary.accessToken && summary.refreshToken) {
        setAccessToken(summary.accessToken);
        setRefreshToken(summary.refreshToken);
        const jwtToken = jwtDecode(summary.accessToken);
        setExpirationTokenDate((jwtToken.exp as number) * 1000);
      }

      navigate(fromPath ?? PATH.DASHBOARD);
    } catch {
      toggleError(true);
      return;
    } finally {
      toggleLoading(false);
    }
  };

  const refreshLogin = async () => {
    toggleLoading(true);
    toggleError(false);
    try {
      const { response, summary } = await refreshCredentialService();

      if (!response.ok && response.status === 401) logout();

      setPermissions(summary.permissions);
      if (summary.accessToken && summary.refreshToken && summary.awsTempCredentials) {
        setAccessToken(summary.accessToken);
        setRefreshToken(summary.refreshToken);
        setAwsTempCredentials(summary.awsTempCredentials);

        const jwtToken = jwtDecode(summary.accessToken);
        setExpirationTokenDate((jwtToken.exp as number) * 1000);
      }
    } catch (error: unknown) {
      toggleError(true);

      if ((error as { response: { status: number } })?.response?.status === 401) logout();

      return;
    } finally {
      toggleLoading(false);
    }
  };

  const logout = () => {
    const returnUrl = window.location.pathname;
    const loginUrl =
      returnUrl !== PATH.LOGIN ? `${PATH.LOGIN}?returnUrl=${encodeURIComponent(returnUrl)}` : PATH.LOGIN;
    clearAuthData();
    navigate(loginUrl);
  };

  return {
    isAuthenticated: Boolean(accessToken && refreshToken),
    isAdmin: Boolean(permissions?.organization?.full),
    login,
    logout,
    refreshLogin: refreshLogin,
    isLoading,
    isError,
  };
}
